import { useEffect, useState } from 'react'
import {
  Checkbox,
  Divider,
  Grid,
  Typography,
  Input,
  FormGroup,
  FormControlLabel,
} from '@yuno/design-system-web'
import { useForm } from 'react-hook-form'
import { Box } from '@mui/system'
import { palette, Theme } from '@yuno/design-system-web'
import styled from '@emotion/styled'
import { WebhookFormFormOptionalFields } from '../../../../typings/pages'
import { useTranslation } from 'react-i18next'

type WebhookFormFormProps = {
  code?: string
  type?: string
  fields?: WebhookFormFormOptionalFields[]
  onSubmit: (code: string, form: any) => void
  onChangeType?: (type: string) => void
  spacing?: number
  setFormValid: (isValid: boolean) => void
  mode?: 'CREATE' | 'EDIT'
  allowedTypes?: {
    enrollment: boolean
    payment: boolean
  }
  webhooksList: any
}

const typeParamMultiChecbox = 'MULTIPLE_CHECKBOX'
const actionTypeEdit = 'EDIT'

const initDefaultValues = (fields) => {
  const obj = {}
  for (let i = 0; i < fields.length; i++) {
    const { id, value, type } = fields[i]
    if (type === typeParamMultiChecbox) {
      obj[id] = value?.map((item) => item.id) || []
    } else {
      obj[id] = value || ''
    }
  }
  return obj
}

const triggerSelectedInitial = (fields) => {
  const triggerSelectedInitial = []
  fields
    .filter((field) => field.type.includes(typeParamMultiChecbox))
    .forEach((field) => {
      field.value.forEach((option) => {
        triggerSelectedInitial.push(`${field.id}_${option.id}`)
      })
    })

  return triggerSelectedInitial
}

const TriggerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0 20px 0;
`

const TriggersContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
`

const ChecboxContainer = styled.div`
  width: 30%;
`

const TriggerTile = styled(Typography)(({ theme }: { theme?: Theme }) => ({
  ...theme.typography.tiny.regular,
  color: theme.palette.neutral.black,
}))

const TriggerCondition = styled(Typography)(({ theme }: { theme?: Theme }) => ({
  ...theme.typography.tiny.regular,
  color: theme.palette.grey[6],
}))

export const WebhooksForm = ({
  code,
  fields,
  onSubmit,
  spacing = 2,
  setFormValid,
  webhooksList,
  mode,
}: WebhookFormFormProps) => {
  const [triggersSelected, setTriggersSelected] = useState(
    mode === actionTypeEdit ? triggerSelectedInitial(fields) : [],
  )
  const { t } = useTranslation()

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      ...initDefaultValues(fields),
    },
  })

  const endpointUrl = 'ENDPOINT_URL'

  useEffect(() => {
    const isEditMode = mode === actionTypeEdit
    const hasNewTriggers = isEditMode
      ? JSON.stringify(triggerSelectedInitial(fields)) !==
        JSON.stringify(triggersSelected)
        ? true
        : false
      : false

    setFormValid(
      (isEditMode ? true : isValid) &&
        (hasNewTriggers || isDirty) &&
        triggersSelected.length > 0,
    )
  }, [isValid, triggersSelected, isDirty])

  const handleOnSubmit = (form: any) => {
    onSubmit(code, form)
  }

  const handleChange = (fieldId, e) => {
    const { value } = e?.target
    const triggerName = `${fieldId.id}_${value}`
    const triggersSelectedSize = triggersSelected.length
    const triggers = triggersSelected.filter(
      (trigger) => trigger !== triggerName,
    )
    if (triggers.length === triggersSelectedSize) {
      setTriggersSelected([...triggersSelected, `${fieldId.id}_${value}`])
    } else {
      setTriggersSelected(triggers)
    }
  }

  const isDefaultChecked = (field, option) =>
    Array.isArray(field?.value)
      ? field?.value?.filter((item) => item['id'] === option.id).length > 0 ||
        false
      : false

  const existName = (value: string): boolean => {
    const exist = webhooksList
      .filter((item) => item.id !== code)
      .filter(
        (webhook) =>
          webhook.name.toLowerCase().trim() === value.toLowerCase().trim(),
      )
    return exist.length > 0 ? false : true
  }

  const inputValidations = {
    urlFormat: {
      value: new RegExp(
        /^(http(s)?:\/\/)[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/i,
      ),
      message: t('validUrl'),
    },
    existeName: {
      existName: (value) => existName(value) || t('nameAlreadyExist'),
    },
    isRequired: {
      value: true,
      message: t('fillField'),
    },
  }

  return (
    <Box>
      <form id="WebhookForm-form" onSubmit={handleSubmit(handleOnSubmit)}>
        <Grid container spacing={spacing} paddingBottom="10px">
          {fields
            .filter((field) => !field.type.includes(typeParamMultiChecbox))
            .map((field, key) => (
              <Grid item xs={12} key={key}>
                <Box display="flex" alignItems={'center'} marginTop={'8px'}>
                  <Input
                    label={
                      field.name_language_code
                        ? t(field.name_language_code)
                        : field.name
                    }
                    placeholder={
                      field.description_language_code
                        ? t(field.description_language_code)
                        : field.description
                    }
                    name={`params.${field.id}`}
                    {...register(field.id as never, {
                      required: inputValidations.isRequired,
                      validate:
                        field.id === 'NAME'
                          ? inputValidations.existeName
                          : null,
                      pattern:
                        field.id === endpointUrl
                          ? inputValidations.urlFormat
                          : null,
                    })}
                    fullWidth
                    error={!!errors[field.id]}
                    helperText={
                      errors[field.id] ? errors[field.id]?.message : ''
                    }
                    type={field.type}
                  />
                </Box>
              </Grid>
            ))}
        </Grid>

        <Grid container>
          <Grid item xs={12}>
            <TriggerHeader>
              <TriggerTile>{t('triggerOn')}</TriggerTile>
              <TriggerCondition>{t('required')}</TriggerCondition>
            </TriggerHeader>
            {fields
              .filter((field) => field.type.includes(typeParamMultiChecbox))
              .map((field, key) => (
                <Grid item xs={12} key={key}>
                  <Divider sx={{ background: palette.grey[2] }} />
                  <Box>
                    <TriggerHeader>
                      <TriggerTile>{field.name}</TriggerTile>
                    </TriggerHeader>
                    <TriggersContainer>
                      {field.options.map((option) => (
                        <ChecboxContainer>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  defaultChecked={isDefaultChecked(
                                    field,
                                    option,
                                  )}
                                  name={option.id}
                                  value={option.id}
                                  {...register(field.id as never, {})}
                                  onChange={handleChange.bind(null, field)}
                                />
                              }
                              label={
                                <Typography variant="caption">
                                  {option.name}
                                </Typography>
                              }
                            />
                          </FormGroup>
                        </ChecboxContainer>
                      ))}
                    </TriggersContainer>
                  </Box>
                </Grid>
              ))}
          </Grid>
        </Grid>
      </form>
    </Box>
  )
}
